import {
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from "react";
import type { MoveItemArgs, TreeItem } from "../primitives/types";

export type BranchHandlers = {
	getItems: () => TreeItem[];
	containsItem: (itemId: string) => boolean;
};

export type TreeRootContextValue = {
	uniqueContextId: symbol;
	registerBranch: (id: string | null, handlers: BranchHandlers) => () => void;
	findItemBranch: (itemId: string) => string | null | undefined;
	getItem: (itemId: string) => TreeItem | undefined;
	getPathToItem: (itemId: string) => string[];
	itemHasChildren: (itemId: string) => boolean;
	onMoveItem: (args: MoveItemArgs) => void;
	subscribeItemOpen: (itemId: string, listener: () => void) => () => void;
	getItemOpen: (itemId: string) => boolean;
	setItemOpen: (itemId: string, open: boolean) => void;
	toggleItemOpen: (itemId: string) => void;
};

export const TreeRootContext = createContext<TreeRootContextValue | null>(null);

export type TreeRootProviderProps = {
	initialOpenState?: Map<string, boolean>;
	onMoveItem?: (args: MoveItemArgs) => void;
	onOpenStateChange?: (itemId: string, isOpen: boolean) => void;
	children: ReactNode;
};

export function TreeRootProvider({
	initialOpenState,
	onMoveItem: onMoveItemProp,
	onOpenStateChange,
	children,
}: TreeRootProviderProps) {
	const branchRegistry = useRef(new Map<string | null, BranchHandlers>());
	const openStateRef = useRef(new Map<string, boolean>(initialOpenState ?? []));
	const openListenersRef = useRef(new Map<string, Set<() => void>>());
	const onMoveItemRef = useRef(onMoveItemProp);
	const onOpenStateChangeRef = useRef(onOpenStateChange);

	useEffect(() => {
		onMoveItemRef.current = onMoveItemProp;
	}, [onMoveItemProp]);

	useEffect(() => {
		onOpenStateChangeRef.current = onOpenStateChange;
	}, [onOpenStateChange]);

	const uniqueContextId = useMemo(() => Symbol("tree-context"), []);

	const notifyItemOpenListeners = useCallback((itemId: string) => {
		const listeners = openListenersRef.current.get(itemId);
		if (listeners) {
			for (const listener of listeners) {
				listener();
			}
		}
	}, []);

	const subscribeItemOpen = useCallback(
		(itemId: string, listener: () => void) => {
			let set = openListenersRef.current.get(itemId);
			if (!set) {
				set = new Set();
				openListenersRef.current.set(itemId, set);
			}
			set.add(listener);
			return () => {
				set?.delete(listener);
				if (set && set.size === 0) {
					openListenersRef.current.delete(itemId);
				}
			};
		},
		[],
	);

	const getItemOpen = useCallback((itemId: string): boolean => {
		return openStateRef.current.get(itemId) ?? false;
	}, []);

	const setItemOpen = useCallback(
		(itemId: string, open: boolean) => {
			const prev = openStateRef.current.get(itemId) ?? false;
			if (prev === open) {
				return;
			}
			openStateRef.current.set(itemId, open);
			notifyItemOpenListeners(itemId);
			onOpenStateChangeRef.current?.(itemId, open);
		},
		[notifyItemOpenListeners],
	);

	const toggleItemOpen = useCallback(
		(itemId: string) => {
			const current = openStateRef.current.get(itemId) ?? false;
			setItemOpen(itemId, !current);
		},
		[setItemOpen],
	);

	const onMoveItem = useCallback((args: MoveItemArgs) => {
		onMoveItemRef.current?.(args);
	}, []);

	const registerBranch = useCallback(
		(id: string | null, handlers: BranchHandlers) => {
			branchRegistry.current.set(id, handlers);
			return () => {
				branchRegistry.current.delete(id);
			};
		},
		[],
	);

	const findItemBranch = useCallback(
		(itemId: string): string | null | undefined => {
			for (const [branchId, handlers] of branchRegistry.current.entries()) {
				if (handlers.containsItem(itemId)) {
					return branchId;
				}
			}
			return undefined;
		},
		[],
	);

	const getPathToItem = useCallback((itemId: string): string[] => {
		const path: string[] = [];

		function findInBranch(branchId: string | null): boolean {
			const handlers = branchRegistry.current.get(branchId);
			if (!handlers) {
				return false;
			}

			const items = handlers.getItems();
			for (const item of items) {
				if (item.id === itemId) {
					return true;
				}
				if (branchRegistry.current.has(item.id)) {
					if (findInBranch(item.id)) {
						path.unshift(item.id);
						return true;
					}
				}
			}
			return false;
		}

		findInBranch(null);
		return path;
	}, []);

	const getItem = useCallback((itemId: string): TreeItem | undefined => {
		for (const handlers of branchRegistry.current.values()) {
			const items = handlers.getItems();
			const item = items.find((i) => i.id === itemId);
			if (item) {
				return item;
			}
		}
		return undefined;
	}, []);

	const itemHasChildren = useCallback((itemId: string): boolean => {
		const handlers = branchRegistry.current.get(itemId);
		if (handlers) {
			return handlers.getItems().length > 0;
		}
		return false;
	}, []);

	const contextValue = useMemo<TreeRootContextValue>(
		() => ({
			uniqueContextId,
			registerBranch,
			findItemBranch,
			getItem,
			getPathToItem,
			itemHasChildren,
			onMoveItem,
			subscribeItemOpen,
			getItemOpen,
			setItemOpen,
			toggleItemOpen,
		}),
		[
			uniqueContextId,
			registerBranch,
			findItemBranch,
			getItem,
			getPathToItem,
			itemHasChildren,
			onMoveItem,
			subscribeItemOpen,
			getItemOpen,
			setItemOpen,
			toggleItemOpen,
		],
	);

	return (
		<TreeRootContext.Provider value={contextValue}>
			{children}
		</TreeRootContext.Provider>
	);
}

export function useTreeRootContext() {
	const context = useContext(TreeRootContext);
	if (!context) {
		throw new Error(
			"useTreeRootContext must be used within a TreeRootProvider",
		);
	}
	return context;
}
